import React, { useEffect, useState } from 'react';

interface AsyncViewProps<T> {
	promise: Promise<T>;
	loadingView: () => React.ReactNode;
	errorView: (error: Error) => React.ReactNode;
	successView: (data: T) => React.ReactNode;
}

/**
 * A component that displays a loading view, an error view, or a success view based on the status of a promise.
 * @param promise The promise to display.
 * @param loadingView The view to display while the promise is loading.
 * @param errorView The view to display if the promise fails.
 * @param successView The view to display if the promise succeeds.
 * @returns The view to display based on the status of the promise.
 */
export function AsyncView<T>({ promise, loadingView, errorView, successView }: AsyncViewProps<T>): React.ReactElement {
	const [state, setState] = useState<{
		status: 'loading' | 'error' | 'success';
		data?: T;
		error?: Error;
	}>({
		status: 'loading',
	});

	useEffect(() => {
		let isMounted = true;

		promise
			.then((data) => {
				if (isMounted) {
					setState({ status: 'success', data });
				}
			})
			.catch((error) => {
				if (isMounted) {
					setState({ status: 'error', error });
				}
			});

		return () => {
			isMounted = false;
		};
	}, [promise]);

	switch (state.status) {
		case 'loading':
			return <>{loadingView()}</>;
		case 'error':
			return <>{errorView(state.error!)}</>;
		case 'success':
			return <>{successView(state.data!)}</>;
	}
}
